/**
 * @typedef {{ id?: string|number, label?: string, answer?: unknown }} ReviewQuestion
 * @typedef {{ id?: string|number, label?: string, questions?: ReviewQuestion[] }} ReviewSubSection
 * @typedef {{ id?: string|number, label?: string, sub_sections?: ReviewSubSection[] }} ReviewSection
 * @typedef {{ id?: string|number, form_name?: string, sections?: ReviewSection[] }} ReviewFormSection
 * @typedef {{ key: string, label: string, form_name: string, section: ReviewSection }} ReviewSectionTab
 * @typedef {{ value: string }} StringRef
 * @typedef {{ value: string[] }} StringArrayRef
 * @typedef {{ active_section_key: StringRef, expanded_form_ids: StringArrayRef }} ReviewState
 */

function normalize_text(value = '') {
  return String(value || '').trim();
}

function get_section_items(form_section = {}) {
  const sections = form_section.sections;
  return Array.isArray(sections) ? sections : [];
}

function get_form_section_id(form_section = {}) {
  return String(form_section.id ?? '');
}

function get_section_key(form_section = {}, section = {}) {
  return `${form_section.id ?? ''}:${section.id ?? ''}`;
}

function build_section_tab(form_section = {}, section = {}) {
  const tab = {
    key: get_section_key(form_section, section),
    label: section.label ?? form_section.form_name ?? 'Section',
    form_name: form_section.form_name ?? '',
    section
  };
  return tab;
}

function has_section_items(form_section = {}) {
  return get_section_items(form_section).length > 0;
}

function first_form_section(form_section = {}) {
  const sections = get_section_items(form_section);
  return sections.length ? sections[0] : null;
}

function first_section_key(form_sections = []) {
  const first_form = form_sections.find(has_section_items);
  const first_section = first_form ? first_form_section(first_form) : null;
  return first_section ? get_section_key(first_form, first_section) : '';
}

/**
 * @param {unknown} form_sections
 * @returns {ReviewFormSection[]}
 */
export function normalize_form_sections(form_sections = []) {
  return Array.isArray(form_sections) ? form_sections : [];
}

/**
 * @param {ReviewFormSection[]} form_sections
 * @returns {ReviewSectionTab[]}
 */
export function build_flattened_section_tabs(form_sections = []) {
  const section_tabs = [];
  form_sections.forEach((form_section) => {
    get_section_items(form_section).forEach((section) => {
      section_tabs.push(build_section_tab(form_section, section));
    });
  });
  return section_tabs;
}

/**
 * @param {ReviewSectionTab[]} section_tabs
 * @param {string} active_key
 * @returns {ReviewSectionTab|null}
 */
export function find_active_section_tab(section_tabs = [], active_key = '') {
  return section_tabs.find((section_tab) => section_tab.key === active_key) || section_tabs[0] || null;
}

function form_sections_have_active_key(form_sections = [], active_key = '') {
  return form_sections.some((form_section) => (
    get_section_items(form_section).some((section) => get_section_key(form_section, section) === active_key)
  ));
}

export function sync_review_state(state, form_sections = []) {
  state.expanded_form_ids.value = form_sections.map(get_form_section_id).filter(Boolean);
  if (!state.active_section_key.value || !form_sections_have_active_key(form_sections, state.active_section_key.value)) {
    state.active_section_key.value = first_section_key(form_sections);
  }
}

function is_form_id_expanded(expanded_ids = [], form_section = {}) {
  return expanded_ids.includes(get_form_section_id(form_section));
}

function toggle_expanded_form_id(expanded_ids = [], form_id = '') {
  return expanded_ids.includes(form_id)
    ? expanded_ids.filter((id) => id !== form_id)
    : [...expanded_ids, form_id];
}

function get_section_questions(section = {}) {
  const sub_sections = section.sub_sections;
  if (!Array.isArray(sub_sections)) return [];
  const questions_list = [];
  sub_sections.forEach((sub_section) => {
    const questions = sub_section ? sub_section.questions : [];
    if (Array.isArray(questions)) questions_list.push(...questions);
  });
  return questions_list;
}

function build_section_status(section = {}) {
  const questions = get_section_questions(section);
  if (!questions.length) return 'No answers';
  const answered_count = questions.filter((question) => normalize_text(question.answer)).length;
  return answered_count === questions.length ? 'Completed' : `${answered_count}/${questions.length}`;
}

function get_sub_section_questions(sub_section = {}) {
  const questions = sub_section.questions;
  return Array.isArray(questions) ? questions : [];
}

function should_display_sub_section_title(sub_section = {}, section = {}) {
  const sub_section_label = normalize_text(sub_section.label).toLowerCase();
  const section_label = normalize_text(section.label).toLowerCase();
  if (!sub_section_label) return false;
  return sub_section_label !== section_label && sub_section_label !== 'details';
}

/**
 * @param {ReviewState} state
 * @returns {Record<string, Function>}
 */
export function build_review_api(state) {
  return {
    section_key: get_section_key,
    is_active_section(form_section = {}, section = {}) {
      return get_section_key(form_section, section) === state.active_section_key.value;
    },
    set_active_section(form_section = {}, section = {}) {
      state.active_section_key.value = get_section_key(form_section, section);
    },
    is_form_expanded(form_section = {}) {
      return is_form_id_expanded(state.expanded_form_ids.value, form_section);
    },
    toggle_form(form_section = {}) {
      const form_id = get_form_section_id(form_section);
      if (!form_id) return;
      state.expanded_form_ids.value = toggle_expanded_form_id(state.expanded_form_ids.value, form_id);
    },
    section_status: build_section_status,
    section_status_tone(section = {}) {
      return build_section_status(section) === 'Completed' ? 'completed' : 'pending';
    },
    format_detail_value(value) {
      return normalize_text(value) || 'Not answered';
    },
    has_answer(question = {}) {
      return Boolean(normalize_text(question.answer));
    },
    sub_section_field_count(sub_section = {}) {
      return get_sub_section_questions(sub_section).length;
    },
    should_show_sub_section_title: should_display_sub_section_title
  };
}

/**
 * @param {ReviewState} state
 * @returns {(form_sections?: ReviewFormSection[]) => void}
 */
export function build_sync_review_state_handler(state) {
  return function handle_review_state_sync(form_sections = []) {
    sync_review_state(state, form_sections);
  };
}
